// oxlint-disable jsx-no-new-object-as-prop

import type { GLOBAL_SETTINGS_QUERY_RESULT } from "@nnf3d/studio/types";
import type { PortableTextComponents } from "@portabletext/react";
import { PortableText } from "@portabletext/react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { AnimatePresence, m } from "motion/react";
import { useCallback, useEffect, useState } from "react";

import { useGlobal } from "../../lib/global-context";
import type { VoidComponent } from "../../lib/types";
import { Button } from "../button";
import { Icon } from "../icon";
import { transitionAnimate, transitionExit } from "./const";
import { Pillar } from "./pillar";

const components = {
	block: { normal: ({ children }) => <span>{children}</span> },
	marks: { strong: ({ children }) => <span className="font-sans font-semibold">{children}</span> },
} as const satisfies PortableTextComponents<
	NonNullable<GLOBAL_SETTINGS_QUERY_RESULT[number]>["title"][number]
>;

const About: VoidComponent = () => {
	const search = useSearch({ from: "__root__" });

	const { title, description, pillars, foundationLink, footerText, footerLinks } = useGlobal();
	const navigate = useNavigate();

	const handleBack = useCallback(() => {
		navigate({ to: ".", search: undefined }).catch(console.error);
	}, [navigate]);

	const handleScrollToTop = useCallback(() => {
		document.querySelector("article")?.scrollTo({ top: 0, behavior: "smooth" });
	}, []);

	const [copyrightText, setCopyrightText] = useState(() => footerText.replaceAll("{year}", ""));

	useEffect(() => {
		// oxlint-disable-next-line react/react-compiler
		setCopyrightText(footerText.replaceAll("{year}", String(new Date().getFullYear())));
	}, [footerText]);

	return (
		<AnimatePresence>
			{search?.about === 1 && (
				<>
					<m.div
						className="absolute top-4 left-4 z-221 *:bg-white desktop:top-8 desktop:left-8"
						initial={{ opacity: 0, y: "1rem" }}
						animate={{ opacity: 1, y: "0rem", transition: transitionAnimate }}
						exit={{ opacity: 0, y: "1rem", transition: transitionExit }}
					>
						<Button icon="nav-arrow-left" onClick={handleBack} />
					</m.div>

					<m.article
						className="fixed inset-0 z-120 no-scrollbar overflow-y-auto bg-surface-primary"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1, transition: transitionAnimate }}
						exit={{ opacity: 0, transition: transitionExit }}
					>
						<div className="mx-auto mt-36 flex max-w-450 flex-col gap-24 px-4 desktop:mt-76 desktop:gap-40 desktop:px-8">
							<div className="flex flex-col gap-2">
								<m.h1
									className="text-xs text-foreground"
									initial={{ opacity: 0, y: "1rem" }}
									animate={{
										opacity: 1,
										y: "0",
										transition: { ...transitionAnimate, delay: 0.05 },
									}}
									exit={{ opacity: 0, y: "1rem", transition: transitionExit }}
								>
									<PortableText value={title} components={components} />
								</m.h1>

								<m.h2
									className="font-serif text-lg text-foreground desktop:text-display-small"
									initial={{ opacity: 0, y: "1rem" }}
									animate={{ opacity: 1, y: "0", transition: { ...transitionAnimate, delay: 0.1 } }}
									exit={{ opacity: 0, y: "1rem", transition: transitionExit }}
								>
									{description}
								</m.h2>
							</div>

							<ul className="flex flex-col gap-24 desktop:gap-40">
								{pillars.map((pillar) => (
									<Pillar key={pillar._key} {...pillar} />
								))}
							</ul>
						</div>

						<footer className="mx-auto mt-60 flex max-w-450 flex-col items-center gap-28 px-4 pb-8 desktop:px-8">
							<div className="flex w-full flex-col items-center gap-8 border-t border-stroke-muted pt-8 desktop:grid desktop:grid-cols-3 desktop:items-center">
								<a
									href={foundationLink}
									target="_blank"
									rel="noopener noreferrer"
									className="items-left flex h-auto text-accent"
								>
									<Icon
										name="novo-nordisk-foundation-logo"
										title="Novo Nordisk Foundation logo"
										className="aspect-249/48 h-10"
									/>
								</a>

								<button
									type="button"
									onClick={handleScrollToTop}
									className="flex cursor-pointer items-center justify-center gap-1 text-center hover:text-foreground-muted"
								>
									<span>Scroll to top</span>

									<Icon name="arrow-up" className="size-5" />
								</button>

								<div className="flex flex-col items-end gap-2 desktop:items-end">
									<p className="text-xs desktop:text-right">{copyrightText}</p>
								</div>
							</div>

							{footerLinks && (
								<ul className="flex max-w-240 flex-wrap justify-center gap-5">
									{footerLinks.map(({ text, url }) => (
										<li key={text}>
											<a
												href={url}
												target="_blank"
												rel="noopener noreferrer"
												className="cursor-pointer hover:text-foreground-muted"
											>
												{text}
											</a>
										</li>
									))}
								</ul>
							)}
						</footer>
					</m.article>
				</>
			)}
		</AnimatePresence>
	);
};

export { About };
